/**
 * The bell pane's **body** (`specs/M5-notifications.md` §5). F4 owns the frame — the 600 px
 * `SlideOverPane`, its header, the tab strip and the filter row — and this fills it with rows
 * drawn to `discovery/ui-reference/notifications/row-chrome.json`.
 *
 * Row heights are content-driven, not a fixed pitch (measured 91 / 111 / 113 / 131 for a
 * one-, two-, three-line and quoted row), so nothing here sets a height.
 */
import { PageEmptyState, PageSkeleton, PaneIcon, RelativeTime } from '@elizmonday/ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { NotificationPage, NotificationRowView, NotificationsSource } from './types';

export interface NotificationsPanelProps {
  source: NotificationsSource;
  tab: 'all' | 'mentioned' | 'assigned';
  unreadOnly: boolean;
  query: string;
  /** Bumped by the realtime hook so a `notification.created` refetches the page. */
  revision?: number;
  onUnreadChange?: (unread: number) => void;
  onOpen?: (row: NotificationRowView) => void;
}

/** monday's three date groups, in its order and with its labels `[ui §1]`. */
export type DateGroup = 'Today' | 'Yesterday' | 'Older Notifications';

export function dateGroupOf(iso: string, now = new Date()): DateGroup {
  const then = new Date(iso);
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const time = then.getTime();
  if (time >= startOfToday) return 'Today';
  if (time >= startOfToday - 86_400_000) return 'Yesterday';
  return 'Older Notifications';
}

export function groupRows(rows: readonly NotificationRowView[], now = new Date()): Array<[DateGroup, NotificationRowView[]]> {
  const order: DateGroup[] = ['Today', 'Yesterday', 'Older Notifications'];
  const buckets = new Map<DateGroup, NotificationRowView[]>();
  for (const row of rows) {
    const group = dateGroupOf(row.createdAt, now);
    const list = buckets.get(group);
    if (list) list.push(row);
    else buckets.set(group, [row]);
  }
  return order.filter((group) => buckets.has(group)).map((group) => [group, buckets.get(group) as NotificationRowView[]]);
}

/** monday's own switch: short (`2m`, `4h`) inside a day, long (`1 day`, `5 months`) beyond. */
export function timeFormatOf(iso: string, now = Date.now()): 'short' | 'long' {
  return now - Date.parse(iso) < 86_400_000 ? 'short' : 'long';
}

const initials = (name: string | null): string =>
  (name ?? '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('') || '?';

export function NotificationRow({
  row,
  onOpen,
  onToggleRead,
  onDelete,
}: {
  row: NotificationRowView;
  onOpen?: (row: NotificationRowView) => void;
  onToggleRead: (row: NotificationRowView) => void;
  onDelete: (row: NotificationRowView) => void;
}) {
  const context = row.itemName ?? row.boardName;
  return (
    <div
      className={`ez-notif-row${row.read ? '' : ' ez-notif-row--unread'}`}
      data-testid={`notification-${row.id}`}
      data-kind={row.kind}
      data-read={row.read ? 'true' : 'false'}
      role="button"
      tabIndex={0}
      onClick={() => onOpen?.(row)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen?.(row);
        }
      }}
    >
      <span className="ez-notif-row__avatar" aria-hidden="true">
        {row.actor?.photoUrl ? (
          <img src={row.actor.photoUrl} alt="" />
        ) : (
          initials(row.actor?.name ?? null)
        )}
      </span>
      <span className="ez-notif-row__body">
        <span className="ez-notif-row__title" data-testid="notification-title">
          {row.title}
        </span>
        {row.text ? <span className="ez-notif-row__text">{row.text}</span> : null}
        {context ? (
          <span className="ez-notif-row__context" data-testid="notification-context">
            {context}
          </span>
        ) : null}
      </span>
      <span className="ez-notif-row__time">
        {/* monday switches units at a day: `2m`, then `1 day`, `1 month`, `5 months`
            (`bell.json`). Anything else reads as a different product. */}
        <RelativeTime value={row.createdAt} format={timeFormatOf(row.createdAt)} />
      </span>
      {row.read ? null : <span className="ez-notif-row__dot" data-testid="notification-unread-dot" />}
      <span className="ez-notif-row__actions">
        <button
          type="button"
          className="ez-notif-row__action"
          aria-label={row.read ? 'Mark as unread' : 'Mark as read'}
          data-testid="notification-mark-read"
          onClick={(event) => {
            event.stopPropagation();
            onToggleRead(row);
          }}
        >
          <PaneIcon name="checkSmall" size={16} />
        </button>
        <button
          type="button"
          className="ez-notif-row__action"
          aria-label="Delete"
          data-testid="notification-delete"
          onClick={(event) => {
            event.stopPropagation();
            onDelete(row);
          }}
        >
          <PaneIcon name="delete" size={16} />
        </button>
      </span>
    </div>
  );
}

export function NotificationsPanel({
  source,
  tab,
  unreadOnly,
  query,
  revision = 0,
  onUnreadChange,
  onOpen,
}: NotificationsPanelProps) {
  const [page, setPage] = useState<NotificationPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const raw = await source.list({ tab, unreadOnly, ...(query ? { query } : {}) });
      // Same defensive shape as the inbox: a stubbed or partial response renders as empty.
      const next: NotificationPage = {
        rows: raw?.rows ?? [],
        nextCursor: raw?.nextCursor ?? null,
        unread: raw?.unread ?? 0,
      };
      setPage(next);
      setError(null);
      onUnreadChange?.(next.unread);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not load notifications');
    } finally {
      setLoading(false);
    }
  }, [source, tab, unreadOnly, query, onUnreadChange]);

  useEffect(() => {
    void load();
  }, [load, revision]);

  const groups = useMemo(() => groupRows(page?.rows ?? []), [page]);

  const toggleRead = useCallback(
    async (row: NotificationRowView) => {
      // Optimistic: the bell is view state, and a failed write is corrected by the next load.
      setPage((current) =>
        current
          ? {
              ...current,
              rows: current.rows.map((entry) => (entry.id === row.id ? { ...entry, read: !row.read } : entry)),
              unread: Math.max(current.unread + (row.read ? 1 : -1), 0),
            }
          : current,
      );
      onUnreadChange?.(Math.max((page?.unread ?? 0) + (row.read ? 1 : -1), 0));
      await source.markRead([row.id], !row.read).catch(() => undefined);
    },
    [source, page, onUnreadChange],
  );

  const remove = useCallback(
    async (row: NotificationRowView) => {
      setPage((current) =>
        current
          ? {
              ...current,
              rows: current.rows.filter((entry) => entry.id !== row.id),
              unread: Math.max(current.unread - (row.read ? 0 : 1), 0),
            }
          : current,
      );
      await source.remove([row.id]).catch(() => undefined);
      await load();
    },
    [source, load],
  );

  if (loading && page === null) return <PageSkeleton variant="list" rows={4} data-testid="notifications-loading" />;
  if (error) {
    return (
      <PageEmptyState
        title="Notifications are unavailable"
        hint={error}
        data-testid="notifications-error"
      />
    );
  }
  if (!page || page.rows.length === 0) {
    return (
      <PageEmptyState
        title="You're all caught up"
        hint={
          tab === 'mentioned'
            ? 'Mentions of you in an update or a reply land here.'
            : tab === 'assigned'
              ? 'Items someone assigns to you land here.'
              : 'Mentions, assignments and board activity land here.'
        }
        data-testid="notifications-empty"
      />
    );
  }

  return (
    <div className="ez-notif-list" data-testid="notifications-list">
      {groups.map(([group, rows]) => (
        <div key={group}>
          <div className="ez-notif-group" data-testid="notification-group">
            {group}
          </div>
          {rows.map((row) => (
            <NotificationRow
              key={row.id}
              row={row}
              {...(onOpen ? { onOpen } : {})}
              onToggleRead={toggleRead}
              onDelete={remove}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
